import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FileDown,
  FileSpreadsheet,
  GlassWater,
  ListOrdered,
  Loader2,
} from "lucide-react";
import { toast } from "react-toastify";
import ReactSelect, { SingleValue, StylesConfig } from "react-select";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns-tz";

import PageHeader from "../../../components/PageHeader";
import { SELECT_STYLES } from "../../../components/DateFilters/constants";
import { BrazilDate } from "../../../utils/BrazilDate";
import { getErrorMessage } from "../../../utils/errorHandler";
import RelatorioPeriodoMaxHint from "../components/RelatorioPeriodoMaxHint";
import RelatorioQueryErrorAlert from "../components/RelatorioQueryErrorAlert";
import { warnIfReportPeriodInvalid } from "../utils/reportPeriodValidation";
import { lojaService } from "../../../services/lojaService";
import {
  fetchRelatorioBebidas,
  RELATORIO_BEBIDAS_PAGE_SIZE,
  type RelatorioBebidasLinha,
} from "../../../services/pedidoIntegracaoService";
import RelatorioSaboresTablePagination from "../components/RelatorioSaboresTablePagination";
import {
  exportRelatorioBebidasPdf,
  exportRelatorioBebidasXlsx,
  type RelatorioBebidasFiltroResumo,
} from "../utils/relatorioBebidasExport";

type LojaOpt = { value: number; label: string };

const LOJA_SELECT_STYLES = SELECT_STYLES as unknown as StylesConfig<
  LojaOpt,
  false
>;

function getDefaultMonthRange() {
  const now = new Date();
  const tz = BrazilDate.getTimezone();
  const yearStr = format(now, "yyyy", { timeZone: tz });
  const monthStr = format(now, "MM", { timeZone: tz });
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthStr, 10);
  const firstDay = BrazilDate.create(year, month, 1);
  const nextMonth = month === 12 ? 1 : month + 1;
  const nextYear = month === 12 ? year + 1 : year;
  const firstDayNextMonth = BrazilDate.create(nextYear, nextMonth, 1);
  const lastDay = new Date(firstDayNextMonth.getTime() - 24 * 60 * 60 * 1000);
  return {
    dataInicio: BrazilDate.formatISO(firstDay),
    dataFim: BrazilDate.formatISO(lastDay),
  };
}

const DEFAULT_MONTH = getDefaultMonthRange();

function formatInt(n: number): string {
  return n.toLocaleString("pt-BR", { maximumFractionDigits: 0 });
}

const Bebidas: React.FC = () => {
  const [startDate, setStartDate] = useState(DEFAULT_MONTH.dataInicio);
  const [endDate, setEndDate] = useState(DEFAULT_MONTH.dataFim);
  const [lojaId, setLojaId] = useState<number | null>(null);
  const [applied, setApplied] = useState<null | {
    dataInicio: string;
    dataFim: string;
    lojaId: number | null;
  }>(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [applied?.dataInicio, applied?.dataFim, applied?.lojaId]);

  const { data: lojas = [] } = useQuery({
    queryKey: ["lojas"],
    queryFn: () => lojaService.getLojas(),
  });

  const lojaOptions = useMemo(
    () => lojas.map((l) => ({ value: l.id, label: l.nome })),
    [lojas]
  );
  const lojaSel = lojaOptions.find((o) => o.value === (lojaId ?? -1)) ?? null;

  const { data, isFetching, isError, error, refetch } = useQuery({
    queryKey: [
      "relatorio-bebidas",
      applied?.dataInicio ?? "no-applied",
      applied?.dataFim ?? "no-applied",
      applied?.lojaId ?? "no-applied",
      page,
      RELATORIO_BEBIDAS_PAGE_SIZE,
    ],
    enabled: applied != null,
    queryFn: () =>
      fetchRelatorioBebidas({
        dataInicio: applied!.dataInicio,
        dataFim: applied!.dataFim,
        lojaId: applied!.lojaId,
        page,
        pageSize: RELATORIO_BEBIDAS_PAGE_SIZE,
      }),
    placeholderData: (prev) => prev,
  });

  useEffect(() => {
    if (isFetching) return;
    if (data != null && typeof data.page === "number" && data.page !== page) {
      setPage(data.page);
    }
  }, [data, page, isFetching]);

  const handleAplicar = () => {
    if (warnIfReportPeriodInvalid(startDate, endDate)) return;

    setApplied({
      dataInicio: startDate,
      dataFim: endDate,
      lojaId,
    });
  };

  const queryErrorMessage = isError ? getErrorMessage(error) : null;

  const pIni =
    applied != null
      ? BrazilDate.formatBR(BrazilDate.fromISOString(applied.dataInicio), false)
      : null;
  const pFim =
    applied != null
      ? BrazilDate.formatBR(BrazilDate.fromISOString(applied.dataFim), false)
      : null;
  const lojaLabel =
    applied != null
      ? applied.lojaId != null
        ? (lojaOptions.find((o) => o.value === applied.lojaId)?.label ?? "—")
        : "Todas"
      : null;

  const linhas = data?.linhas ?? [];
  const totalBebidas = data?.totalBebidas ?? 0;
  const totalLinhas = data?.totalLinhas ?? 0;
  const pageSize = data?.pageSize ?? RELATORIO_BEBIDAS_PAGE_SIZE;
  const totalPages = totalLinhas === 0 ? 1 : Math.ceil(totalLinhas / pageSize);

  const bebidasNaPagina = useMemo(
    () => (data?.linhas ?? []).reduce((s, r) => s + r.quantidade, 0),
    [data]
  );

  const filtrosExport = useMemo((): RelatorioBebidasFiltroResumo[] => {
    return [{ label: "Loja", value: lojaLabel ?? "—" }];
  }, [lojaLabel]);

  const handleExportPdf = useCallback(async () => {
    if (!applied) {
      toast.warning("Aplique os filtros antes de exportar.");
      return;
    }
    try {
      const full = await fetchRelatorioBebidas({
        dataInicio: applied.dataInicio,
        dataFim: applied.dataFim,
        lojaId: applied.lojaId,
        todasLinhas: true,
      });
      if (full.linhas.length === 0) {
        toast.warning("Não há linhas para exportar.");
        return;
      }
      exportRelatorioBebidasPdf({
        periodoInicioISO: applied.dataInicio,
        periodoFimISO: applied.dataFim,
        filtros: filtrosExport,
        linhas: full.linhas.map((r: RelatorioBebidasLinha) => ({
          lojaNome: r.lojaNome,
          bebidaNome: r.bebidaNome,
          quantidade: r.quantidade,
        })),
        totalBebidas: full.totalBebidas,
        geradoEmLabel: BrazilDate.formatBR(new Date(), true),
      });
      toast.success("PDF baixado.");
    } catch {
      toast.error("Não foi possível gerar o PDF.");
    }
  }, [applied, filtrosExport]);

  const handleExportExcel = useCallback(async () => {
    if (!applied) {
      toast.warning("Aplique os filtros antes de exportar.");
      return;
    }
    try {
      const full = await fetchRelatorioBebidas({
        dataInicio: applied.dataInicio,
        dataFim: applied.dataFim,
        lojaId: applied.lojaId,
        todasLinhas: true,
      });
      if (full.linhas.length === 0) {
        toast.warning("Não há linhas para exportar.");
        return;
      }
      exportRelatorioBebidasXlsx({
        periodoInicioISO: applied.dataInicio,
        periodoFimISO: applied.dataFim,
        filtros: filtrosExport,
        linhas: full.linhas.map((r: RelatorioBebidasLinha) => ({
          lojaNome: r.lojaNome,
          bebidaNome: r.bebidaNome,
          quantidade: r.quantidade,
        })),
        totalBebidas: full.totalBebidas,
        geradoEmLabel: BrazilDate.formatBR(new Date(), true),
      });
      toast.success("Planilha Excel baixada.");
    } catch {
      toast.error("Não foi possível gerar o Excel.");
    }
  }, [applied, filtrosExport]);

  return (
    <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        title="Bebidas"
        description=""
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => void handleExportExcel()}
              disabled={!applied || totalLinhas === 0 || isFetching}
              className="inline-flex items-center gap-2 rounded-lg border border-emerald-600 bg-white px-3 py-2 text-sm font-semibold text-emerald-800 shadow-sm transition-opacity hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FileSpreadsheet size={18} />
              Excel
            </button>
            <button
              type="button"
              onClick={() => void handleExportPdf()}
              disabled={!applied || totalLinhas === 0 || isFetching}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FileDown size={18} />
              PDF (A4)
            </button>
          </div>
        }
      />

      <div className="mb-6 rounded-lg border border-neutral-200 bg-neutral-50/80 p-4">
        <p className="mb-3 text-sm font-medium text-neutral-800">Filtros</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="flex flex-col gap-1">
            <label
              htmlFor="bebidas-ini"
              className="text-xs font-medium text-neutral-700"
            >
              Data início
            </label>
            <input
              id="bebidas-ini"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-primary"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label
              htmlFor="bebidas-fim"
              className="text-xs font-medium text-neutral-700"
            >
              Data fim
            </label>
            <input
              id="bebidas-fim"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-primary"
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-neutral-700">Loja</span>
            <ReactSelect<LojaOpt, false>
              inputId="bebidas-loja"
              value={lojaSel}
              onChange={(o: SingleValue<LojaOpt>) =>
                setLojaId(o?.value ?? null)
              }
              options={lojaOptions}
              isClearable
              placeholder="Todas"
              styles={LOJA_SELECT_STYLES}
              className="text-sm"
              noOptionsMessage={() => "Nenhuma loja"}
            />
          </div>
        </div>
        <RelatorioPeriodoMaxHint />
        <button
          type="button"
          onClick={handleAplicar}
          className="mt-4 inline-flex rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-90"
        >
          Aplicar filtros
        </button>
      </div>

      {applied != null ? (
        <p className="mb-4 text-sm text-neutral-600">
          Período: <span className="font-medium text-neutral-800">{pIni}</span>{" "}
          a <span className="font-medium text-neutral-800">{pFim}</span>
          {" · "}
          Loja:{" "}
          <span className="font-medium text-neutral-800">{lojaLabel}</span>
          {totalBebidas > 0 && (
            <>
              {" · "}
              Total de bebidas:{" "}
              <span className="font-medium text-neutral-800">
                {formatInt(totalBebidas)}
              </span>
            </>
          )}
        </p>
      ) : (
        <p className="mb-4 text-sm text-neutral-600">
          Selecione o período e clique em <strong>Aplicar filtros</strong>.
        </p>
      )}

      <div className="relative">
        {applied != null && isFetching && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/60">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}

        {applied != null ? (
          <section
            className="rounded-2xl border border-neutral-200/90 bg-white p-4 shadow-sm sm:p-6"
            aria-label="Bebidas vendidas"
          >
            <div className="mb-4 flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <ListOrdered size={22} strokeWidth={2} />
              </div>
              <div className="min-w-0">
                <h2 className="flex items-center gap-2 text-base font-semibold text-neutral-900">
                  <GlassWater
                    className="h-5 w-5 text-primary"
                    strokeWidth={2}
                  />
                  Totais por loja e bebida
                </h2>
                <p className="mt-1 text-sm text-neutral-600">
                  Conta bebidas avulsas (ex.: Heineken long neck, Bebidas Lata)
                  e opções de refrigerante em combos/pizzas (ex.: Coca cola zero
                  no Big Trem).
                </p>
                <p className="mt-1 text-xs text-neutral-500">
                  {RELATORIO_BEBIDAS_PAGE_SIZE} linhas por página; exportações
                  incluem todas as linhas do período.
                </p>
              </div>
            </div>

            {queryErrorMessage ? (
              <RelatorioQueryErrorAlert message={queryErrorMessage} />
            ) : (
            <div className="overflow-x-auto rounded-xl border border-neutral-100">
              <table className="w-full min-w-[420px] text-left text-sm">
                <thead>
                  <tr className="border-b border-neutral-200 bg-neutral-50/90">
                    <th className="px-3 py-2.5 font-semibold text-neutral-800 sm:px-4">
                      Loja
                    </th>
                    <th className="px-3 py-2.5 font-semibold text-neutral-800 sm:px-4">
                      Bebida
                    </th>
                    <th className="px-3 py-2.5 text-right font-semibold text-neutral-800 sm:px-4">
                      Quantidade
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {linhas.length === 0 ? (
                    <tr>
                      <td
                        colSpan={3}
                        className="px-4 py-10 text-center text-sm text-neutral-500"
                      >
                        Nenhuma bebida no período para os filtros.
                      </td>
                    </tr>
                  ) : (
                    linhas.map((row, i) => (
                      <tr
                        key={`${row.lojaNome}-${row.bebidaNome}-${i}`}
                        className="border-b border-neutral-100 last:border-b-0"
                      >
                        <td className="px-3 py-2.5 text-neutral-900 sm:px-4">
                          {row.lojaNome}
                        </td>
                        <td className="px-3 py-2.5 text-neutral-900 sm:px-4">
                          {row.bebidaNome}
                        </td>
                        <td className="px-3 py-2.5 text-right font-medium tabular-nums text-neutral-900 sm:px-4">
                          {formatInt(row.quantidade)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                {linhas.length > 0 ? (
                  <tfoot className="border-t-2 border-neutral-200 bg-neutral-50/90">
                    <tr>
                      <td
                        colSpan={2}
                        className="px-3 py-2.5 text-sm font-semibold text-neutral-800 sm:px-4"
                      >
                        Total de bebidas (período)
                      </td>
                      <td className="px-3 py-2.5 text-right text-sm font-bold tabular-nums text-neutral-900 sm:px-4">
                        {formatInt(totalBebidas)}
                      </td>
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>
            )}
            {!queryErrorMessage && data != null && totalLinhas > 0 && (
              <RelatorioSaboresTablePagination
                page={data.page}
                totalPages={totalPages}
                totalRegistros={totalLinhas}
                totalGeralFmt={formatInt(totalBebidas)}
                totalPaginaFmt={formatInt(bebidasNaPagina)}
                isLoading={isFetching}
                onPrevious={() => setPage((p) => Math.max(1, p - 1))}
                onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
              />
            )}
          </section>
        ) : null}

        {applied != null ? (
          <button
            type="button"
            onClick={() => void refetch()}
            className="mt-6 text-sm font-medium text-primary hover:underline"
          >
            Recarregar dados
          </button>
        ) : null}
      </div>
    </main>
  );
};

export default Bebidas;
